/**
 * Managed option lists — pure helpers and the two adapters.
 *
 * `ManagedSelect` (components/forms) renders every dropdown on the drafting
 * surfaces from one of these lists. What it decides — what is shown, in
 * which order, what a click does to the value, whether "+ Add" is offered —
 * lives here so it is tested without a DOM.
 *
 *   defaultAdapter         /option-lists/{key} (backend services/option_lists.py)
 *   documentTypesAdapter   /voice/brief/document-types (the catalogue's own store)
 *   adapterFor(key)        the adapter a list key uses unless one is passed
 */
import type {
  OptionItem,
  OptionList,
  OptionListAdapter,
  OptionValueInput,
  PayloadFieldSpec,
} from './types';

export type * from './types';

/** The prefix an org value's key carries. */
export const ORG_PREFIX = 'org:';

export const isOrgKey = (key: string | null | undefined): boolean =>
  typeof key === 'string' && key.startsWith(ORG_PREFIX);

/** Normalise the component's value to a list of keys. */
export function valueKeys(value: string | readonly string[] | null | undefined): string[] {
  if (value == null) return [];
  if (typeof value === 'string') return value ? [value] : [];
  return value.filter((v) => typeof v === 'string' && v.length > 0);
}

/**
 * What picking an item does: multi toggles it in (order of picking kept) or
 * out; single replaces, and picking the chosen one again keeps it.
 */
export function nextValue(
  current: string | readonly string[] | null | undefined,
  key: string,
  multiple: boolean,
): string | string[] {
  const keys = valueKeys(current);
  if (!multiple) return key;
  return keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key];
}

/** Remove one key (a chip's ×). */
export function withoutKey(
  current: string | readonly string[] | null | undefined,
  key: string,
  multiple: boolean,
): string | string[] | null {
  if (!multiple) return null;
  return valueKeys(current).filter((k) => k !== key);
}

const norm = (s: string) => s.normalize('NFKD').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * The rows the dropdown shows: active values only (hidden built-ins and
 * archived org values belong to Manage values), filtered by the query over
 * label, key and description, built-ins first in the server's order, then
 * the org's own by position.
 */
export function visibleItems(list: OptionList | null | undefined, query = ''): OptionItem[] {
  if (!list) return [];
  const q = norm(query);
  const rows = list.items.filter((i) => !i.hidden && !i.archived);
  const matched = q
    ? rows.filter(
        (i) =>
          norm(i.label).includes(q) ||
          norm(i.key).includes(q) ||
          norm(i.description ?? '').includes(q),
      )
    : rows;
  const builtins = matched.filter((i) => i.builtin);
  const own = matched
    .filter((i) => !i.builtin)
    .slice()
    .sort((a, b) => a.position - b.position || a.label.localeCompare(b.label));
  return [...builtins, ...own];
}

/** Whether the "+ Add …" row is offered at all. */
export function canAdd(list: OptionList | null | undefined, allowAdd = true): boolean {
  if (!list || !allowAdd) return false;
  if (!list.allow_add || !list.permissions.can_add) return false;
  return list.managed_by !== 'clause_library';
}

/** Whether a typed query already names a row exactly (no duplicate add). */
export function exactMatch(list: OptionList | null | undefined, query: string): OptionItem | null {
  const q = norm(query);
  if (!q || !list) return null;
  return list.items.find((i) => norm(i.label) === q) ?? null;
}

/** Whether "Manage values" has anything to offer this person. */
export function canManage(list: OptionList | null | undefined): boolean {
  if (!list) return false;
  if (list.permissions.can_hide_builtins && list.allow_hide) return true;
  return list.items.some((i) => !i.builtin && (i.can_edit || i.can_delete));
}

/**
 * Labels for the chosen keys — from the list where present, else from the
 * `resolved` map (archived / hidden values a record still holds), else the
 * key itself, flagged so the chip can say it is no longer offered.
 */
export function chosenLabels(
  list: OptionList | null | undefined,
  keys: readonly string[],
  resolved: Readonly<Record<string, { label: string; archived?: boolean; hidden?: boolean }>> = {},
): { key: string; label: string; inactive: boolean }[] {
  return keys.map((key) => {
    const item = list?.items.find((i) => i.key === key);
    if (item) return { key, label: item.label, inactive: item.archived || item.hidden };
    const r = resolved[key];
    if (r) return { key, label: r.label, inactive: Boolean(r.archived || r.hidden) };
    return { key, label: key.startsWith(ORG_PREFIX) ? key.slice(ORG_PREFIX.length) : key, inactive: true };
  });
}

/** Keys a value holds that the loaded list does not show (to resolve). */
export function unresolvedKeys(list: OptionList | null | undefined, keys: readonly string[]): string[] {
  if (!list) return [];
  const shown = new Set(list.items.filter((i) => !i.hidden && !i.archived).map((i) => i.key));
  return keys.filter((k) => !shown.has(k));
}

/** Client-side validation before an add / edit is sent: field → message. */
export function validateInput(
  list: OptionList,
  input: OptionValueInput,
  opts: { editingKey?: string } = {},
): Record<string, string> {
  const errors: Record<string, string> = {};
  const label = (input.label ?? '').replace(/\s+/g, ' ').trim();
  if (list.single_word) {
    if (!/^\S{1,60}$/u.test(label)) errors.label = 'One word, up to 60 characters.';
  } else if (label.length < 2) {
    errors.label = 'Give it a name of at least two characters.';
  } else if (label.length > 200) {
    errors.label = 'Up to 200 characters.';
  }
  const dup = list.items.find((i) => norm(i.label) === norm(label) && i.key !== opts.editingKey);
  if (!errors.label && dup) {
    errors.label = dup.archived
      ? `“${dup.label}” is archived — restore it in Manage values.`
      : `“${dup.label}” is already in the list.`;
  }
  for (const f of list.payload_fields) {
    const v = (input.payload?.[f.name] ?? '').trim();
    if (!v) {
      if (f.required) errors[`payload.${f.name}`] = `${f.label} is required.`;
      continue;
    }
    if (v.length > f.max_len) errors[`payload.${f.name}`] = `${f.label}: up to ${f.max_len} characters.`;
    if (f.choices.length && !f.choices.includes(v)) {
      errors[`payload.${f.name}`] = `${f.label}: choose one of ${f.choices.join(', ')}.`;
    }
  }
  return errors;
}

/** Payload fields the inline form asks for (required first). */
export function formFields(list: OptionList | null | undefined): PayloadFieldSpec[] {
  if (!list) return [];
  return [...list.payload_fields].sort((a, b) => Number(b.required) - Number(a.required));
}

/** The message a failed write shows (the server's sentence when it sent one). */
export function writeErrorMessage(err: unknown): string {
  const body = (err as { body?: { detail?: unknown } } | null)?.body;
  const d = body?.detail;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const m = (d as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  if (Array.isArray(d) && d[0] && typeof d[0].msg === 'string') return d[0].msg;
  if (typeof d === 'string' && d) return d;
  return 'That could not be saved. Try again.';
}

// ── Adapters ───────────────────────────────────────────────────────────────

const enc = encodeURIComponent;

export const defaultAdapter: OptionListAdapter = {
  id: 'option-lists',
  fetch: (api, listKey, opts) => {
    const qs = new URLSearchParams();
    if (opts.includeHidden) qs.set('include_hidden', 'true');
    if (opts.includeArchived) qs.set('include_archived', 'true');
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return api<OptionList>(`/option-lists/${enc(listKey)}${tail}`);
  },
  create: (api, listKey, input) =>
    api<OptionItem>(`/option-lists/${enc(listKey)}/values`, { method: 'POST', body: input }),
  update: (api, listKey, item, patch) =>
    api<OptionItem>(`/option-lists/${enc(listKey)}/values/${enc(item.id ?? item.key)}`, {
      method: 'PATCH',
      body: patch,
    }),
  archive: (api, listKey, item) =>
    api<OptionItem>(`/option-lists/${enc(listKey)}/values/${enc(item.id ?? item.key)}/archive`, {
      method: 'POST',
    }),
  restore: (api, listKey, item) =>
    api<OptionItem>(`/option-lists/${enc(listKey)}/values/${enc(item.id ?? item.key)}/restore`, {
      method: 'POST',
    }),
  setHidden: (api, listKey, item, hidden) =>
    api<OptionItem>(`/option-lists/${enc(listKey)}/builtin/${enc(item.key)}/${hidden ? 'hide' : 'show'}`, {
      method: 'POST',
    }),
};

interface CatalogueItem {
  key: string;
  slug: string;
  label: string;
  group: string;
  source: 'builtin' | 'org';
  description?: string | null;
  is_active: boolean;
  hidden?: boolean;
  id?: string | null;
  created_by_user_id?: string | null;
  can_edit: boolean;
  updated_at?: string | null;
}

interface Catalogue {
  items: CatalogueItem[];
  can_add: boolean;
  is_admin?: boolean;
}

/** The catalogue item → the option shape. Exported for the tests. */
export function catalogueItemToOption(i: CatalogueItem): OptionItem {
  const builtin = i.source === 'builtin';
  return {
    key: i.key,
    value_key: i.slug,
    id: builtin ? null : (i.id ?? null),
    label: i.label,
    description: i.description ?? null,
    payload: { group: i.group },
    builtin,
    hidden: builtin ? Boolean(i.hidden) : false,
    archived: builtin ? false : !i.is_active,
    position: 0,
    created_by_user_id: i.created_by_user_id ?? null,
    can_edit: !builtin && i.can_edit,
    can_delete: !builtin && i.can_edit,
    can_hide: builtin && i.can_edit,
    updated_at: i.updated_at ?? null,
  };
}

/** A type added inline from a dropdown is filed under Custom, with no
 *  required details; Manage types edits the rest. */
export const INLINE_DOCUMENT_TYPE_GROUP = 'Custom';

export const documentTypesAdapter: OptionListAdapter = {
  id: 'document-types',
  fetch: async (api, _listKey, opts) => {
    const archived = opts.includeArchived || opts.includeHidden;
    const cat = await api<Catalogue>(
      `/voice/brief/document-types${archived ? '?include_archived=true' : ''}`,
    );
    return {
      list_key: 'document_types',
      label: 'Document types',
      description: 'The drafting catalogue.',
      multiple: false,
      allow_add: true,
      allow_hide: true,
      managed_by: 'document_types',
      manage_href: null,
      add_label: 'Add type',
      single_word: false,
      payload_fields: [],
      permissions: { can_add: cat.can_add, can_hide_builtins: Boolean(cat.is_admin), is_admin: Boolean(cat.is_admin) },
      items: cat.items.map(catalogueItemToOption),
    };
  },
  create: async (api, _listKey, input) =>
    catalogueItemToOption(
      await api<CatalogueItem>('/voice/brief/document-types', {
        method: 'POST',
        body: {
          label: input.label,
          group_label: INLINE_DOCUMENT_TYPE_GROUP,
          description: input.description ?? null,
          required_fields: [],
        },
      }),
    ),
  update: async (api, _listKey, item, patch) =>
    catalogueItemToOption(
      await api<CatalogueItem>(`/voice/brief/document-types/${enc(item.id ?? '')}`, {
        method: 'PATCH',
        body: {
          ...(patch.label !== undefined ? { label: patch.label } : {}),
          ...(patch.description !== undefined ? { description: patch.description } : {}),
        },
      }),
    ),
  archive: async (api, _listKey, item) =>
    catalogueItemToOption(
      await api<CatalogueItem>(`/voice/brief/document-types/${enc(item.id ?? '')}/archive`, { method: 'POST' }),
    ),
  restore: async (api, _listKey, item) =>
    catalogueItemToOption(
      await api<CatalogueItem>(`/voice/brief/document-types/${enc(item.id ?? '')}/restore`, { method: 'POST' }),
    ),
  setHidden: async (api, _listKey, item, hidden) =>
    catalogueItemToOption(
      await api<CatalogueItem>(
        `/voice/brief/document-types/builtin/${enc(item.key)}/${hidden ? 'hide' : 'show'}`,
        { method: 'POST' },
      ),
    ),
};

/** The adapter a list key uses unless the caller passes one. */
export function adapterFor(listKey: string): OptionListAdapter {
  return listKey === 'document_types' ? documentTypesAdapter : defaultAdapter;
}

